import type React from "react";
import {observer} from "mobx-react-lite";
import {createContext, useContext, useMemo, useState} from "react";
import {Button, CircularProgress, IconButton, Stack, Tooltip, Typography} from "@mui/material";
import {Check, Close} from "@mui/icons-material";
import EditIcon from '~/icons/svg/EditSquareIcon.svg?react'
import DeleteIcon from '~/icons/svg/DeleteIcon.svg?react'
import type {GridColDef} from "@mui/x-data-grid-premium";
import {Tag} from "~/components/tag/Tag";
import {CustomDataGrid} from "~/components/data-grid/CustomDataGrid";
import {CurrencyComponent} from "~/components/currency-component/CurrencyComponent";
import {SupplierCountryFlag} from "~/pages/supplier-360/components/SupplierCountryFlag";
import {CountryColumnHeader} from "~/pages/supplier-360/components/CountryColumnHeader";
import {SupplierNamePicker} from "~/pages/supplier-360/components/SupplierNamePicker";
import type {SupplierNameOption} from "~/pages/supplier-360/components/supplierNameOption";
import {useSupplierClusterMembersQuery} from "~/pages/supplier-360/hooks/useSupplierClusterMembersQuery";
import {useCreateSupplierClusterMemberMutation} from "~/pages/supplier-360/tabs/SupplierOverview/hooks/useCreateSupplierClusterMemberMutation";
import {useRemoveSupplierClusterMemberMutation} from "~/pages/supplier-360/tabs/SupplierOverview/hooks/useRemoveSupplierClusterMemberMutation";
import {authStore} from "~/stores/init";
import {SUPPLIER_MATCH_BASIS_LABEL} from "~/pages/supplier-360/constants/supplierEntityLabels";
import {
    SUPPLIER_CLUSTER_MEMBER_MATCH_BASES,
    type SupplierClusterMember,
} from "~/pages/supplier-360/types/Supplier360Types";
import {COLORS} from "~/style/colors-ts";

const MATCH_BASIS_OPTIONS = SUPPLIER_CLUSTER_MEMBER_MATCH_BASES.map(value => ({
    value,
    label: SUPPLIER_MATCH_BASIS_LABEL[value],
}));

const BLANK_LABEL = '—';
const NAME_OPTIONS_PAGE_SIZE = 20;

/** Negative so it can never collide with a real `ClusterMember.id`. */
const DRAFT_ROW_ID = -1;

/** The unsaved row the "Add new cluster" button puts at the top of the table. */
type ClusterDraftRow = {
    id: typeof DRAFT_ROW_ID;
    kind: 'draft';
    normalized_name: string;
};

type ClusterRow = SupplierClusterMember | ClusterDraftRow;

type RowKind = 'draft' | 'saved';

function rowKind(row: ClusterRow): RowKind {
    return (row as {kind?: RowKind}).kind ?? 'saved';
}

function isSavedRow(row: ClusterRow): row is SupplierClusterMember {
    return rowKind(row) === 'saved';
}

type DraftApi = {
    options: SupplierNameOption[];
    optionsLoading: boolean;
    typedName: string;
    onSearch: (term: string) => void;
    onOpenChange: (open: boolean) => void;
    onTypeName: (value: string) => void;
    onPick: (option: SupplierNameOption) => void;
    canSave: boolean;
    isSaving: boolean;
    onCommit: () => void;
    onDiscard: () => void;
};

/**
 * The draft row holds a controlled input, so its behaviour reaches the cell through context
 * rather than through the column definitions: a new `columns` array on every keystroke would
 * make the DataGrid re-create the cell and the input would lose focus mid-word. The draft's
 * *values* ride on the row object (which is meant to change per keystroke); only its *behaviour*
 * comes from here, which leaves `getClusterColumns` dependent on `rows` alone.
 */
const DraftContext = createContext<DraftApi | null>(null);

function useDraftApi(): DraftApi {
    const api = useContext(DraftContext);
    if (!api) throw new Error('Draft cells can only render inside a DraftContext provider');
    return api;
}

/**
 * The same picker the Identity section's Parent field uses, minus `allowAdd`: a cluster row has
 * to reference a raw row that already exists, so there is no "+ Add" path here.
 */
const DraftRawNameCell: React.FC = () => {
    const {
        options,
        optionsLoading,
        typedName,
        onSearch,
        onOpenChange,
        onTypeName,
        onPick,
        onDiscard,
    } = useDraftApi();
    return (
        <SupplierNamePicker
            options={options}
            loading={optionsLoading}
            onSearch={onSearch}
            onOpenChange={onOpenChange}
            variant="standard"
            placeholder="Search supplier name"
            emptyText="No matching raw supplier"
            autoFocus
            inputValue={typedName}
            onInputChange={onTypeName}
            onSelect={onPick}
            onEscape={onDiscard}
        />
    );
};

const DraftActionsCell: React.FC = () => {
    const {canSave, isSaving, onCommit, onDiscard} = useDraftApi();
    return (
        <Stack direction="row" gap={0.5}>
            <IconButton size="small" title="Add row" disabled={!canSave || isSaving} onClick={onCommit}>
                {isSaving ? <CircularProgress size={16}/> : <Check fontSize="small"/>}
            </IconButton>
            <IconButton size="small" title="Discard" disabled={isSaving} onClick={onDiscard}>
                <Close fontSize="small"/>
            </IconButton>
        </Stack>
    );
};

type RowActionsApi = {
    onRemove: (row: SupplierClusterMember) => void;
    isRemoving: (row: SupplierClusterMember) => boolean;
};

/**
 * The saved rows' delete action, reaching the cell the same way the draft's behaviour does and for
 * the same reason (see `DraftContext`): `isRemoving` flips while a removal is in flight, and a
 * `columns` array rebuilt on that would re-create the draft's input and drop focus mid-word.
 */
const RowActionsContext = createContext<RowActionsApi | null>(null);

function useRowActions(): RowActionsApi {
    const api = useContext(RowActionsContext);
    if (!api) throw new Error('Row action cells can only render inside a RowActionsContext provider');
    return api;
}

/**
 * Only rendered in edit mode. Removing is `split` (docs §2.1), not a row delete — the raw row
 * survives and goes back to being unassigned; it is this cluster's claim on it that goes.
 */
const RemoveRowCell: React.FC<{row: SupplierClusterMember}> = ({row}) => {
    const {onRemove, isRemoving} = useRowActions();
    const removing = isRemoving(row);
    return (
        <IconButton
            size="small"
            title="Remove raw row"
            disabled={removing}
            onClick={() => onRemove(row)}
        >
            {removing ? <CircularProgress size={16}/> : <DeleteIcon fontSize="small"/>}
        </IconButton>
    );
};

/** Every column but the name is server-derived, so the unsaved draft row just shows a dash. */
const BlankCell: React.FC<{title: string}> = ({title}) => (
    <Tooltip title={title}><span>{BLANK_LABEL}</span></Tooltip>
);

const NOT_YET_RESOLVED = 'Set from the source record once the row is saved';

/**
 * Filtering is client-side here: the whole cluster is already in memory (one `cluster_member`
 * call per entity), and the endpoint itself filters on nothing useful — only `entity` /
 * `s_id` exact (docs §2.2). So the select options are the values actually present in this
 * cluster; blanks are left out, since a `''` option would read as "no filter".
 */
function distinctOptions(values: string[]): {value: string, label: string}[] {
    return [...new Set(values.filter(Boolean))]
        .sort((a, b) => a.localeCompare(b))
        .map(value => ({value, label: value}));
}

/** `name` is the editable label and `code` the stable id; either can stand in for a row's Source. */
function sourceLabel(row: SupplierClusterMember): string {
    return row.data_source?.name || row.data_source?.code || '';
}

function getClusterColumns(rows: SupplierClusterMember[], isEditMode: boolean): GridColDef<ClusterRow>[] {
    const countryNames = new Map(rows.map(r => [r.country?.code_a2 ?? '', r.country?.name ?? BLANK_LABEL]));

    return [
        {
            // Live name from the raw ERP data (`s_name`), not the frozen `normalized_name` this row
            // was clustered under — falls back to `normalized_name` for a manually-added row, whose
            // synthetic id has no matching `data_source.Supplier` to join against (added 2026-10-20).
            field: 's_name', headerName: 'Supplier name', flex: 1.5, minWidth: 240, sortable: false,
            valueGetter: (_value, row) => isSavedRow(row) ? (row.s_name || row.normalized_name) : '',
            renderCell: ({row}) => isSavedRow(row)
                ? <Tag variant="outlined" color="default" label={row.s_name || row.normalized_name}/>
                : <DraftRawNameCell/>,
        },
        {
            // The source system's own supplier id; a hand-added row gets a `manual-…` id (docs §2.2).
            field: 's_id', headerName: 'Supplier ID', flex: 0.7, minWidth: 130, sortable: false,
            valueGetter: (_value, row) => isSavedRow(row) ? row.s_id : '',
            renderCell: ({row}) => isSavedRow(row)
                ? row.s_id || BLANK_LABEL
                : <BlankCell title={NOT_YET_RESOLVED}/>,
        },
        {
            field: 'data_source',
            headerName: 'Source',
            type: 'singleSelect',
            valueOptions: distinctOptions(rows.map(r => sourceLabel(r))),
            flex: 0.6,
            minWidth: 140,
            sortable: false,
            valueGetter: (_value, row) => isSavedRow(row) ? sourceLabel(row) : '',
            renderCell: ({row}) => isSavedRow(row)
                ? sourceLabel(row) || BLANK_LABEL
                : <BlankCell title={NOT_YET_RESOLVED}/>,
        },
        {
            field: 'country',
            headerName: 'Country',
            type: 'singleSelect',
            valueOptions: distinctOptions(rows.map(r => r.country?.code_a2 ?? ''))
                .map(({value}) => ({value, label: countryNames.get(value) ?? value})),
            flex: 0.4,
            minWidth: 120,
            align: 'center',
            renderHeader: () => <CountryColumnHeader/>,
            sortable: false,
            valueGetter: (_value, row) => isSavedRow(row) ? row.country?.code_a2 ?? '' : '',
            renderCell: ({row}) => isSavedRow(row)
                ? <SupplierCountryFlag country={row.country}/>
                : <BlankCell title={NOT_YET_RESOLVED}/>,
        },
        {
            field: 'vat', headerName: 'VAT', flex: 0.7, minWidth: 130, sortable: false,
            valueGetter: (_value, row) => isSavedRow(row) ? row.vat : '',
            renderCell: ({row}) => isSavedRow(row)
                ? row.vat || BLANK_LABEL
                : <BlankCell title={NOT_YET_RESOLVED}/>,
        },
        {
            field: 'spend',
            headerName: 'Spend',
            type: 'number',
            headerAlign: 'left',
            flex: 0.6,
            minWidth: 110,
            sortable: false,
            // This raw row's own spend, not the cluster total — computed server-side (docs §2.2).
            valueGetter: (_value, row) => isSavedRow(row) ? Number(row.spend ?? 0) : 0,
            renderCell: ({row, value}) => isSavedRow(row)
                ? <CurrencyComponent v={value} nDigits={3}/>
                : <BlankCell title="Calculated from the row's own transactions"/>,
        },
        {
            field: 'match_basis',
            headerName: 'Match basis',
            type: 'singleSelect',
            valueOptions: MATCH_BASIS_OPTIONS,
            flex: 0.7,
            minWidth: 120,
            sortable: false,
            valueGetter: (_value, row) => isSavedRow(row) ? row.match_basis : 'manual',
            renderCell: ({row}) => isSavedRow(row)
                ? (
                    <Tag
                        size="small"
                        variant="outlined"
                        color="default"
                        label={SUPPLIER_MATCH_BASIS_LABEL[row.match_basis] ?? row.match_basis}
                    />
                )
                : <BlankCell title="Recorded as a manual match once the row is saved"/>,
        },
        // Read-only outside edit mode: no column at all rather than an empty one, so the table
        // gives the other columns the space back.
        ...(isEditMode ? [{
            field: 'actions',
            headerName: '',
            width: 84,
            align: 'center',
            sortable: false,
            filterable: false,
            disableExport: true,
            renderCell: ({row}) => isSavedRow(row) ? <RemoveRowCell row={row}/> : <DraftActionsCell/>,
        } satisfies GridColDef<ClusterRow>] : []),
    ];
}

type ClusterSectionProps = {
    rows: SupplierClusterMember[];
    count: number;
    isLoading: boolean;
    entityId: number;
    databagId: number;
};

export const ClusterSection: React.FC<ClusterSectionProps> = observer(({
                                                                           rows,
                                                                           isLoading,
                                                                           entityId,
                                                                           databagId,
                                                                       }) => {
    const canEdit = authStore.hasEditPermission;
    const [isEditMode, setIsEditMode] = useState(false);
    const [isDrafting, setIsDrafting] = useState(false);
    const [typedName, setTypedName] = useState('');
    const [search, setSearch] = useState('');
    const [isPickerOpen, setIsPickerOpen] = useState(false);
    const [picked, setPicked] = useState<SupplierNameOption | null>(null);

    const {mutate: createMember, isPending: isSaving} = useCreateSupplierClusterMemberMutation();
    const {
        mutate: removeMember,
        isPending: isRemoving,
        variables: removingVariables,
    } = useRemoveSupplierClusterMemberMutation();

    /**
     * Raw rows across the whole databag (`cluster_member/?databag_id=&search=`, §2.2) — the same
     * list the registry's Raw view pages through, not just this entity's own cluster. Fetched only
     * while the dropdown is open.
     */
    const {data: rawMatches, isFetching: optionsLoading} = useSupplierClusterMembersQuery(
        databagId,
        {search: search || undefined, page: 1, pageSize: NAME_OPTIONS_PAGE_SIZE},
        {enabled: isPickerOpen},
    );

    // Each candidate says which supplier it currently resolves to, so picking one is an informed
    // choice rather than a name in isolation. Rows already in this cluster are never useful picks —
    // dropped by entity id rather than by name, since two clusters can hold the same `normalized_name`.
    const options: SupplierNameOption[] = useMemo(() => {
        const alreadyHere = new Set(rows.map(r => r.normalized_name));
        return (rawMatches?.results ?? [])
            .filter(m => m.normalized_name && m.entity?.id !== entityId && !alreadyHere.has(m.normalized_name))
            .map(m => ({
                entityId: m.entity?.id ?? null,
                name: m.normalized_name,
                hint: m.entity?.canonical_name ? `now in ${m.entity.canonical_name}` : 'unassigned',
            }));
    }, [rawMatches, rows, entityId]);

    const canSave = picked !== null;

    const closeDraft = () => {
        setIsDrafting(false);
        setTypedName('');
        setSearch('');
        setIsPickerOpen(false);
        setPicked(null);
    };

    const commit = () => {
        if (!picked || isSaving) return;
        createMember(
            {entity_id: entityId, normalized_name: picked.name.trim()},
            {onSuccess: closeDraft},
        );
    };

    const draftApi: DraftApi = {
        options,
        optionsLoading,
        typedName,
        onSearch: setSearch,
        onOpenChange: setIsPickerOpen,
        onTypeName: value => {
            setTypedName(value);
            // Typing past a selection invalidates it — the picker is pick-only, so a half-typed
            // name must not stay committable.
            setPicked(null);
        },
        onPick: option => {
            setPicked(option);
            setTypedName(option.name);
        },
        canSave,
        isSaving,
        onCommit: commit,
        onDiscard: closeDraft,
    };

    const rowActionsApi: RowActionsApi = {
        onRemove: row => {
            if (isRemoving) return;
            removeMember({entityId, sId: row.s_id, displayName: row.s_name || row.normalized_name});
        },
        // `variables` holds the in-flight call's own payload, so the spinner lands on the row
        // actually being removed rather than on every row at once.
        isRemoving: row => isRemoving && removingVariables?.sId === row.s_id,
    };

    // Select options are derived from the saved rows, so the columns are rebuilt when the cluster
    // changes — but deliberately not when the draft does. See `DraftContext`. Edit mode is in here
    // too because it adds the actions column; it only flips on Edit/Done, never mid-draft.
    const columns = useMemo(() => getClusterColumns(rows, isEditMode), [rows, isEditMode]);

    const draftRow: ClusterDraftRow = {id: DRAFT_ROW_ID, kind: 'draft', normalized_name: typedName};
    // Saved rows come back from the refetch the mutation triggers — nothing is held locally.
    // Highest spend first, like every Supplier 360 table — sorted here rather than through the grid's
    // sort model so the draft row stays pinned on top instead of sinking with its zero spend.
    const sortedRows = useMemo(() => [...rows].sort((a, b) => Number(b.spend ?? 0) - Number(a.spend ?? 0)), [rows]);
    const gridRows: ClusterRow[] = isDrafting ? [draftRow, ...sortedRows] : sortedRows;

    return (
        <>
            <Stack direction="row" alignItems="center" gap={1} sx={{mt: 2}}>
                <Typography
                    variant="body2"
                    component="h3"
                    fontWeight={700}
                >
                    Cluster
                </Typography>

                {canEdit && (isEditMode
                    ? (
                        <Button
                            size="small"
                            variant="contained"
                            startIcon={<Check fontSize="small"/>}
                            disabled={isSaving || isRemoving}
                            onClick={() => {
                                closeDraft();
                                setIsEditMode(false);
                            }}
                            sx={{ml: 'auto'}}
                        >
                            Done
                        </Button>
                    )
                    : (
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<EditIcon fontSize="small"/>}
                            disabled={isLoading}
                            onClick={() => {
                                setIsEditMode(true);
                                setIsDrafting(true);
                            }}
                            sx={{ml: 'auto'}}
                        >
                            Edit cluster
                        </Button>
                    ))}
            </Stack>

            <DraftContext.Provider value={draftApi}>
                <RowActionsContext.Provider value={rowActionsApi}>
                    <CustomDataGrid
                        layoutKey="supplier-360-clusters"
                        rows={gridRows}
                        columns={columns}
                        loading={isLoading}
                        getRowClassName={({row}) => rowKind(row) === 'draft' ? 'draft-row' : ''}
                        showToolbar
                        toolbarProps={{showFilterPanel: false}}
                        disableColumnMenu
                        disableRowSelectionOnClick
                        autoHeight
                        sx={{
                            '& .draft-row': {bgcolor: COLORS.primary[100]},
                        }}
                    />
                </RowActionsContext.Provider>
            </DraftContext.Provider>
        </>
    );
});
